import {
  type JpuDTO,
  type ReadServerStatusResponse,
  type SubscriptionMeta,
  ViewContext,
} from "@shared/types/common";
import { subscriptionsService } from "@shared/services/subscriptions";
import window from "@shared/utils/app-window";
import { useSubscriptionsStore } from "@shared/stores/subscriptions";
import { useNotifications } from "@shared/module-transformers/useNotifications.ts";
import { messagesService } from "@shared/services/messages.ts";
import { useServerMetaDataStore } from "@shared/stores/serverMetaData.ts";
import logger from "@shared/utils/logger.ts";
import { useJpdStore } from "@shared/stores/jpd.ts";
import { JpdTableRow } from "@shared/components/jpd-table-selector/jpdTableSelector.ts";
import { TagProps } from "element-plus";

export enum JpdStatus {
  ACTIVE = "Active",
  IN_PROGRESS = "In Progress",
  INACTIVE = "Inactive",
  PENDING = "Pending",
}

export interface JpdStatusTag {
  tagType: TagProps["type"];
  status: JpdStatus;
}

export const jpdUtils = {
  getAccessibleJpus: () => {
    const subscription = useSubscriptionsStore().currentSubscription;
    if (!subscription) {
      return [];
    }

    return subscription.jpus.filter((jpd) => jpdUtils.isJpdAccessible(jpd));
  },
  getTechnicalNameByServerName: (serverName: string) => {
    const subscription = useSubscriptionsStore().currentSubscription;
    if (!subscription) {
      return undefined;
    }

    const jpuDTO = subscription.jpus.find(
      (jpu) => jpu.serverName === serverName,
    );

    if (!jpuDTO) {
      return undefined;
    }

    return jpuDTO.technicalServerName;
  },
  getServerNameByTechnicalName: (technicalServerName: string) => {
    const subscription = useSubscriptionsStore().currentSubscription;
    if (!subscription) {
      return undefined;
    }

    // First, search in main jpus
    let jpuDTO = subscription.jpus.find(
      (jpu) => jpu.technicalServerName === technicalServerName,
    );

    // If there are related JPD, we should check these also
    if (!jpuDTO) {
      for (const jpu of subscription.jpus) {
        if (jpu.relatedJpus && jpu.relatedJpus.length > 0) {
          jpuDTO = jpu.relatedJpus.find(
            (relatedJpu) =>
              relatedJpu.technicalServerName === technicalServerName,
          );
          if (jpuDTO) {
            break;
          }
        }
      }
    }

    if (!jpuDTO) {
      return undefined;
    }

    return jpuDTO.serverName;
  },
  toJpdDisplayName: (technicalServerName: string): string =>
    jpdUtils.getServerNameByTechnicalName(technicalServerName) ||
    technicalServerName,
  toJpdDisplayNames: (technicalServerNames: string[]): string[] =>
    technicalServerNames.map(jpdUtils.toJpdDisplayName),
  getCurrentSubscriptionTechNames: (serverNames?: string[]) => {
    const subscription = useSubscriptionsStore().currentSubscription;
    if (!subscription) {
      return [];
    }
    if (!serverNames) {
      return subscription.jpus.map((server) => server.technicalServerName);
    }

    return subscription.jpus
      .filter((jpu) => serverNames.includes(jpu.serverName))
      .map((server) => server.technicalServerName);
  },
  getCurrentSubscriptionServerNames: (techServerNames?: string[]) => {
    const subscription = useSubscriptionsStore().currentSubscription;
    if (!subscription) {
      return [];
    }
    if (!techServerNames) {
      return subscription.jpus.map((server) => server.serverName);
    }

    return subscription.jpus
      .filter((jpu) => techServerNames.includes(jpu.technicalServerName))
      .map((server) => server.serverName);
  },
  getColdStorageTechnicalServerNames: () => {
    const subscription = useSubscriptionsStore().currentSubscription;
    if (!subscription) {
      return [];
    }

    const allJpus: JpuDTO[] = [];
    subscription.jpus.forEach((jpu) => {
      allJpus.push(jpu);
      if (jpu.relatedJpus && jpu.relatedJpus.length > 0) {
        allJpus.push(...jpu.relatedJpus);
      }
    });

    return allJpus
      .filter((jpu) => jpdUtils.isColdJdp(jpu))
      .map((jpu) => jpu.technicalServerName);
  },
  getJpdType: (jpd: JpuDTO) => {
    if (!jpdUtils.isPlatformJpd(jpd)) return "edge";

    if (jpd.isMothership) {
      return "mothership";
    }

    return "platform";
  },
  getJpdDisplayType: (jpd: JpuDTO): JpdTableRow["type"] =>
    jpdUtils.isPlatformJpd(jpd) ? "Platform" : "Distribution Edge",
  getJpdStatusTag: (
    jpd: JpuDTO,
    activeServersStatus: ReadServerStatusResponse,
  ): JpdStatusTag => {
    const inProgressTag: JpdStatusTag = {
      tagType: "warning",
      status: JpdStatus.IN_PROGRESS,
    };

    if (
      jpd.status === "ACTIVE" &&
      isServerStatusInProgress(jpd.technicalServerName, activeServersStatus)
    ) {
      return inProgressTag;
    }
    if (jpd.status === "ACTIVE") {
      return { tagType: "success", status: JpdStatus.ACTIVE };
    }
    if (jpd.status === "FAILED" || jpd.status === "NEW") {
      return inProgressTag;
    }
    if (
      jpd.status === "PENDING" &&
      isServerStatusInProgress(jpd.technicalServerName, activeServersStatus)
    ) {
      return inProgressTag;
    }
    if (jpd.status === "PENDING") {
      return { tagType: "warning", status: JpdStatus.PENDING };
    }
    return { tagType: "info", status: JpdStatus.INACTIVE };
  },

  isJpdStatusInProgress: (
    jpd: JpuDTO,
    activeServersStatus: ReadServerStatusResponse,
  ): boolean =>
    jpdUtils.getJpdStatusTag(jpd, activeServersStatus).status ===
    JpdStatus.IN_PROGRESS,

  isJpdStatusActive: (
    jpd: JpuDTO,
    activeServersStatus: ReadServerStatusResponse,
  ): boolean =>
    jpdUtils.getJpdStatusTag(jpd, activeServersStatus).status ===
    JpdStatus.ACTIVE,

  isPlatformJpd: (jpd: JpuDTO) => {
    return !jpdUtils.isEdgeJpd(jpd) && !jpdUtils.isColdJdp(jpd);
  },

  isEdgeJpd: (jpd: JpuDTO) => {
    return jpd.products.some((product) => product.productName === "edge");
  },
  isColdJdp: (jpd: JpuDTO) => {
    return jpd.products.some((product) => product.productName === "cold");
  },
  // TODO: have also in security
  fetchServerStatus: async (
    accountNumber: string,
    jpds: JpuDTO[],
  ): Promise<ReadServerStatusResponse> => {
    const activeJpds = jpds.filter(
      (jpd) => jpd.status === "ACTIVE" || jpd.status === "PENDING",
    );
    if (!activeJpds.length) {
      return {};
    }
    return await subscriptionsService.getServersStatusParallel(accountNumber, {
      technicalServerNames: activeJpds.map((jpd) => jpd.technicalServerName),
    });
  },
  goToPlatform: (serverName: string, target: "_blank" | "_self" = "_blank") => {
    const url = `https://${serverName}.${window.PLATFORM_DOMAIN}/`;
    window.open(url, target);
  },
  isMothership: (jpu: JpuDTO): boolean =>
    jpu.isMothership || jpu.products.some((p) => p.productName === "jfmc"),
  isJpdNotAccessible: (jpdItem: JpuDTO): boolean => {
    const isCurrentSubscriptionActive =
      useSubscriptionsStore().isCurrentSubscriptionActive;

    const isJpdDeleted = jpdItem.status === "DELETED";
    const isJpdBlocked = jpdItem.status === "BLOCKED";

    const isSubscriptionActiveAndJpdBlocked =
      isCurrentSubscriptionActive && isJpdBlocked;

    return isJpdDeleted || isSubscriptionActiveAndJpdBlocked;
  },
  isJpdAccessible: (jpdItem: JpuDTO): boolean => {
    return !jpdUtils.isJpdNotAccessible(jpdItem);
  },
  isIPAllowlistAccessible: () => {
    const subscription = useSubscriptionsStore().currentSubscription;
    const meta = subscription?.meta;

    return (
      meta?.isEnterprise ||
      meta?.isEnterpriseX ||
      meta?.isEnterprisePlus ||
      meta?.isEnterpriseTeam
    );
  },

  filterByJpd: (
    isSelfHosted: SubscriptionMeta["isSelfHosted"],
    options: string[],
  ) => {
    return isSelfHosted
      ? (options as string[]).map((option) => {
          const label = option.split("-").pop() || option;

          return { value: option, label: `JPD-${label}` };
        })
      : options?.map((server) => {
          return { value: server, label: server };
        });
  },

  fetchMetaDataForJpdsIfEmpty: async () => {
    const subscriptionStore = useSubscriptionsStore();
    const serverMetaDataStore = useServerMetaDataStore();
    if (!subscriptionStore.currentSubscription) {
      return [];
    }
    const activeJpds = subscriptionStore.currentSubscription.jpus.filter(
      (jpd) => jpd.status === "ACTIVE",
    );

    serverMetaDataStore.setIsLoadingServerMetaDataRequest(true);
    const techServerNamesWithFetchedMetaDataInfo = Object.keys(
      serverMetaDataStore.serverMetaData,
    );
    const isMetaDataStoreIncludesAllJpdsInfo = activeJpds.every((jpd) =>
      techServerNamesWithFetchedMetaDataInfo.includes(jpd.technicalServerName),
    );
    if (isMetaDataStoreIncludesAllJpdsInfo) {
      serverMetaDataStore.setIsLoadingServerMetaDataRequest(false);
      return;
    }
    try {
      const technicalServerNames = activeJpds.map(
        (jpd) => jpd.technicalServerName,
      );
      const readServerIPMetaResponse =
        await subscriptionsService.getServersParallelMetaData(
          subscriptionStore.currentSubscriptionNumber,
          {
            technicalServerNames,
          },
        );
      serverMetaDataStore.setServerMetaData(readServerIPMetaResponse);
    } catch (e) {
      logger.error(messagesService.app_something_went_wrong, e);
      useNotifications().error(messagesService.app_something_went_wrong);
    }
    serverMetaDataStore.setIsLoadingServerMetaDataRequest(false);
  },

  setDefaultCurrentJpds: () => {
    const subscription = useSubscriptionsStore().currentSubscription;
    if (!subscription) {
      return;
    }

    const jpdStore = useJpdStore();
    const defaultJpd = subscriptionsService.extractMainSaasServer(subscription);

    if (!defaultJpd) {
      logger.error("No main server found for the current subscription");
      return;
    }

    Object.keys(ViewContext).forEach((context) => {
      jpdStore.setCurrentJpdForContext({
        jpd: defaultJpd,
        context: context as ViewContext,
      });
    });
  },
  sortPerType: (a: JpuDTO, b: JpuDTO) =>
    a.isMothership || (jpdUtils.isPlatformJpd(a) && !jpdUtils.isPlatformJpd(b))
      ? -1
      : 1,
};

const isServerStatusInProgress = (
  technicalServerName: JpuDTO["technicalServerName"],
  activeServersStatus: ReadServerStatusResponse,
): boolean => {
  return (
    technicalServerName in activeServersStatus &&
    activeServersStatus[technicalServerName].inProgress
  );
};
